/*
 * ABOKPower Storefront Cart
 *
 * Shopify's locale-aware Ajax Cart API is the only cart data source in this
 * theme. Liquid provides semantic entry points and mounts; this runtime owns
 * serialized mutations, normalization, rendering, and checkout redirects.
 */
(function storefrontCartRuntime(global) {
  'use strict';

  const config = global.storefrontCartConfig || {};
  const strings = config.strings || {};
  const locale = config.locale || document.documentElement.lang || 'en';
  const rootPath = String(config.routes?.rootUrl || global.Shopify?.routes?.root || '/');
  const rootUrl = rootPath === '/' ? '' : rootPath.replace(/\/$/, '');
  const paidAttributeKeys = new Set(['_abok_device_id_v1', '_abok_ad_touch_id_v1', '_abok_attribution_id']);

  function jsonRoute(value, fallback) {
    const route = String(value || fallback || '');
    return route.endsWith('.js') ? route : `${route}.js`;
  }

  const cartUrl = String(config.routes?.cartUrl || global.routes?.cart_url || `${rootUrl}/cart`);
  const endpoints = Object.freeze({
    cart: jsonRoute(config.routes?.cartJsonUrl, cartUrl),
    add: jsonRoute(config.routes?.cartAddUrl || global.routes?.cart_add_url, `${rootUrl}/cart/add`),
    change: jsonRoute(config.routes?.cartChangeUrl || global.routes?.cart_change_url, `${rootUrl}/cart/change`),
    update: jsonRoute(config.routes?.cartUpdateUrl || global.routes?.cart_update_url, `${rootUrl}/cart/update`),
  });
  const checkoutUrl = String(config.routes?.checkoutUrl || `${rootPath.endsWith('/') ? rootPath : `${rootPath}/`}checkout`);

  class StorefrontCartError extends Error {
    constructor(message, options = {}) {
      super(message);
      this.name = 'StorefrontCartError';
      this.code = options.code || 'STOREFRONT_CART_ERROR';
      this.details = options.details || null;
    }
  }

  let currentCart = null;
  let initialized = false;
  let busyOperations = 0;
  let operationQueue = Promise.resolve();
  let readyPromise = null;
  const noteTimers = new WeakMap();

  function enqueue(operation) {
    const queuedOperation = operationQueue.then(operation, operation);
    operationQueue = queuedOperation.catch(() => undefined);
    return queuedOperation;
  }

  function emit(type, detail = {}) {
    document.dispatchEvent(
      new CustomEvent(`storefront-cart:${type}`, {
        detail,
      })
    );
  }

  function setBusy(isBusy, source = 'storefront-cart') {
    busyOperations = Math.max(0, busyOperations + (isBusy ? 1 : -1));
    const loading = busyOperations > 0;
    document.querySelectorAll('[data-storefront-cart-root]').forEach((element) => {
      element.toggleAttribute('aria-busy', loading);
    });
    emit('loading', { loading, source });
  }

  function getLines(cart = currentCart) {
    return cart?.lines?.edges?.map((edge) => edge?.node).filter(Boolean) || [];
  }

  function numericId(value) {
    const id = String(value || '').trim();
    if (/^\d+$/.test(id)) return Number(id);
    const match = id.match(/\/(\d+)(?:\?|$)/);
    return match ? Number(match[1]) : null;
  }

  function productVariantGid(value) {
    const id = String(value || '').trim();
    if (!id) throw new StorefrontCartError(strings.missingVariant || 'Choose a product option.', { code: 'MISSING_VARIANT' });
    return id.startsWith('gid://shopify/ProductVariant/') ? id : `gid://shopify/ProductVariant/${id}`;
  }

  function sellingPlanGid(value) {
    const id = String(value || '').trim();
    if (!id) return undefined;
    return id.startsWith('gid://shopify/SellingPlan/') ? id : `gid://shopify/SellingPlan/${id}`;
  }

  function normalizeQuantity(value, fallback = 1) {
    const quantity = Number.parseInt(value, 10);
    if (!Number.isFinite(quantity) || quantity < 1) return fallback;
    return quantity;
  }

  function normalizeAttributes(attributes) {
    if (!attributes) return [];
    if (Array.isArray(attributes)) {
      return attributes
        .filter((attribute) => attribute && attribute.key && attribute.value !== undefined)
        .map((attribute) => ({ key: String(attribute.key), value: String(attribute.value) }));
    }
    return Object.entries(attributes)
      .filter(([, value]) => value !== undefined && value !== null && String(value) !== '')
      .map(([key, value]) => ({ key: String(key), value: String(value) }));
  }

  function attributesObject(attributes) {
    return Object.fromEntries(normalizeAttributes(attributes).map(({ key, value }) => [key, value]));
  }

  function normalizeLine(line) {
    if (!line || typeof line !== 'object') {
      throw new StorefrontCartError(strings.invalidLine || 'The cart item is invalid.', { code: 'INVALID_LINE' });
    }
    const id = numericId(line.merchandiseId || line.id || line.variantId);
    if (!id) {
      throw new StorefrontCartError(strings.missingVariant || 'Choose a product option.', { code: 'MISSING_VARIANT' });
    }
    const normalized = {
      id,
      quantity: normalizeQuantity(line.quantity),
    };
    const attributes = normalizeAttributes(line.attributes || line.properties);
    const sellingPlanId = numericId(line.sellingPlanId || line.selling_plan);
    if (attributes.length) normalized.properties = attributesObject(attributes);
    if (sellingPlanId) normalized.selling_plan = sellingPlanId;
    return normalized;
  }

  function linesFromForm(form) {
    if (!(form instanceof HTMLFormElement)) {
      throw new StorefrontCartError(strings.invalidForm || 'The product form is unavailable.', { code: 'INVALID_FORM' });
    }
    const formData = new FormData(form);
    const attributes = [];
    for (const [name, rawValue] of formData.entries()) {
      const match = name.match(/^properties\[(.+)]$/);
      if (!match || rawValue instanceof File || String(rawValue) === '') continue;
      attributes.push({ key: match[1], value: String(rawValue) });
    }
    return [
      normalizeLine({
        merchandiseId: formData.get('id'),
        quantity: formData.get('quantity') || 1,
        sellingPlanId: formData.get('selling_plan'),
        attributes,
      }),
    ];
  }

  async function ajaxRequest(url, options = {}) {
    let response;
    try {
      response = await fetch(url, {
        method: options.method || 'GET',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Accept-Language': locale,
          ...(options.body ? { 'Content-Type': 'application/json' } : {}),
        },
        ...(options.body ? { body: JSON.stringify(options.body) } : {}),
      });
    } catch (error) {
      throw new StorefrontCartError(strings.networkError || 'The cart is temporarily unavailable.', {
        code: 'NETWORK_ERROR',
        details: error,
      });
    }

    let body;
    try {
      body = await response.json();
    } catch (error) {
      throw new StorefrontCartError(strings.invalidResponse || 'Shopify returned an invalid cart response.', {
        code: 'INVALID_RESPONSE',
        details: error,
      });
    }

    if (!response.ok) {
      const message = body?.description || body?.message || strings.cartError || 'The cart request failed.';
      throw new StorefrontCartError(message, {
        code: `HTTP_${response.status}`,
        details: body,
      });
    }
    return body;
  }

  function moneyFromCents(cents, currencyCode) {
    return {
      amount: (Number(cents || 0) / 100).toFixed(2),
      currencyCode,
    };
  }

  function normalizeDiscountAllocations(item, currencyCode) {
    const allocations = Array.isArray(item?.line_level_discount_allocations)
      ? item.line_level_discount_allocations
      : [];
    if (allocations.length) {
      return allocations.map((allocation) => ({
        title: allocation.discount_application?.title || '',
        code: allocation.discount_application?.type === 'discount_code'
          ? allocation.discount_application?.title || ''
          : '',
        discountedAmount: moneyFromCents(allocation.amount, currencyCode),
      }));
    }
    return (item?.discounts || []).map((discount) => ({
      title: discount.title || '',
      discountedAmount: moneyFromCents(discount.amount, currencyCode),
    }));
  }

  function normalizeAjaxLine(item, currencyCode) {
    const quantity = normalizeQuantity(item.quantity);
    const variantId = numericId(item.variant_id || item.id);
    const productId = numericId(item.product_id);
    const finalUnitPrice = Number(item.final_price ?? item.price ?? 0);
    const originalUnitPrice = Number(item.original_price ?? item.price ?? finalUnitPrice);
    const finalLinePrice = Number(item.final_line_price ?? item.line_price ?? finalUnitPrice * quantity);
    const originalLinePrice = Number(item.original_line_price ?? originalUnitPrice * quantity);
    const image = item.featured_image || {};
    const selectedOptions = Array.isArray(item.options_with_values)
      ? item.options_with_values.map(({ name, value }) => ({ name: String(name || ''), value: String(value || '') }))
      : (item.variant_options || []).map((value, index) => ({ name: `Option ${index + 1}`, value: String(value || '') }));
    const sellingPlan = item.selling_plan_allocation?.selling_plan;

    return {
      id: String(item.key || variantId || ''),
      quantity,
      attributes: normalizeAttributes(item.properties),
      discountAllocations: normalizeDiscountAllocations(item, currencyCode),
      cost: {
        amountPerQuantity: moneyFromCents(finalUnitPrice, currencyCode),
        subtotalAmount: moneyFromCents(originalLinePrice, currencyCode),
        totalAmount: moneyFromCents(finalLinePrice, currencyCode),
      },
      sellingPlanAllocation: sellingPlan
        ? {
            sellingPlan: {
              id: sellingPlanGid(sellingPlan.id),
              name: String(sellingPlan.name || ''),
            },
          }
        : null,
      merchandise: {
        id: productVariantGid(variantId),
        title: String(item.variant_title || 'Default Title'),
        sku: String(item.sku || ''),
        availableForSale: true,
        quantityRule: {
          increment: Number(item.quantity_rule?.increment || 1),
          minimum: Number(item.quantity_rule?.min || 1),
          maximum: item.quantity_rule?.max == null ? null : Number(item.quantity_rule.max),
        },
        image: (item.image || image.url)
          ? {
              url: String(image.url || item.image || ''),
              altText: String(image.alt || item.product_title || ''),
              width: Number(image.width || 160),
              height: Number(image.height || 160),
            }
          : null,
        price: moneyFromCents(finalUnitPrice, currencyCode),
        compareAtPrice: originalUnitPrice > finalUnitPrice
          ? moneyFromCents(originalUnitPrice, currencyCode)
          : null,
        selectedOptions,
        product: {
          id: productId ? `gid://shopify/Product/${productId}` : '',
          handle: String(item.handle || ''),
          title: String(item.product_title || item.title || ''),
          vendor: String(item.vendor || ''),
        },
      },
    };
  }

  function normalizeDiscountCodes(cart) {
    const result = [];
    const seen = new Set();
    const add = (code, applicable = true) => {
      const value = String(code || '').trim();
      const key = value.toLowerCase();
      if (!value || seen.has(key)) return;
      seen.add(key);
      result.push({ code: value, applicable: Boolean(applicable) });
    };
    (cart.discount_codes || []).forEach(({ code, applicable }) => add(code, applicable !== false));
    (cart.cart_level_discount_applications || [])
      .filter((discount) => discount?.type === 'discount_code')
      .forEach((discount) => add(discount.title, true));
    return result;
  }

  function normalizeAjaxCart(cart) {
    if (!cart || !Array.isArray(cart.items)) {
      throw new StorefrontCartError(strings.invalidResponse || 'Shopify returned an invalid cart response.', {
        code: 'INVALID_RESPONSE',
        details: cart,
      });
    }
    const currencyCode = String(cart.currency || config.currencyCode || 'USD');
    return {
      totalQuantity: Number(cart.item_count || 0),
      note: String(cart.note || ''),
      attributes: normalizeAttributes(cart.attributes),
      discountCodes: normalizeDiscountCodes(cart),
      cost: {
        subtotalAmount: moneyFromCents(cart.items_subtotal_price ?? cart.original_total_price, currencyCode),
        totalAmount: moneyFromCents(cart.total_price, currencyCode),
      },
      lines: {
        edges: cart.items.map((item) => ({ node: normalizeAjaxLine(item, currencyCode) })),
        pageInfo: { hasNextPage: false, endCursor: null },
      },
    };
  }

  async function fetchCartInternal() {
    return normalizeAjaxCart(await ajaxRequest(endpoints.cart));
  }

  async function synchronizePaidAttributes(cart) {
    if (!cart || !global.AbokPaidAdTouch) return cart;
    const desired = normalizeAttributes(global.AbokPaidAdTouch.cartAttributes());
    const existing = cart.attributes || [];
    const retained = existing.filter(({ key }) => !paidAttributeKeys.has(key));
    const attributes = retained.concat(desired);
    if (existing.length === attributes.length &&
      existing.every(({ key, value }) => attributes.some((next) => next.key === key && next.value === value))) {
      return cart;
    }
    const updateAttributes = attributesObject(attributes);
    existing
      .filter(({ key }) => paidAttributeKeys.has(key) && !desired.some((attribute) => attribute.key === key))
      .forEach(({ key }) => {
        updateAttributes[key] = '';
      });
    return normalizeAjaxCart(await ajaxRequest(endpoints.update, {
      method: 'POST',
      body: { attributes: updateAttributes },
    }));
  }

  function escapeHTML(value) {
    return String(value ?? '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }

  function formatMoney(money) {
    const amount = Number(money?.amount || 0);
    const currency = money?.currencyCode || config.currencyCode || 'USD';
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
      }).format(amount);
    } catch (_error) {
      return `${amount.toFixed(2)} ${currency}`;
    }
  }

  function moneyToCents(money) {
    return Math.round(Number(money?.amount || 0) * 100);
  }

  function publicSnapshot(cart = currentCart) {
    if (!cart) return null;
    return {
      totalQuantity: cart.totalQuantity || 0,
      note: cart.note || '',
      buyerIdentity: { countryCode: cart.buyerIdentity?.countryCode || null },
      discountCodes: (cart.discountCodes || []).map(({ code, applicable }) => ({ code, applicable })),
      cost: cart.cost,
      lines: getLines(cart).map((line) => ({
        id: line.id,
        quantity: line.quantity,
        attributes: line.attributes,
        discountAllocations: line.discountAllocations || [],
        cost: line.cost,
        sellingPlanAllocation: line.sellingPlanAllocation,
        merchandise: line.merchandise,
      })),
    };
  }

  function dawnCart(cart = currentCart, meta = {}) {
    const lines = getLines(cart);
    const currency = cart?.cost?.totalAmount?.currencyCode || config.currencyCode || 'USD';
    return {
      item_count: cart?.totalQuantity || 0,
      currency,
      total_price: moneyToCents(cart?.cost?.totalAmount),
      original_total_price: moneyToCents(cart?.cost?.subtotalAmount),
      items: lines.map((line) => {
        const variantId = numericId(line.merchandise?.id);
        return {
          id: variantId,
          variant_id: variantId,
          key: line.id,
          quantity: line.quantity,
          title: line.merchandise?.product?.title || '',
          variant_title: line.merchandise?.title || '',
          vendor: line.merchandise?.product?.vendor || '',
          url: `${rootUrl}/products/${line.merchandise?.product?.handle || ''}`,
          image: line.merchandise?.image?.url || null,
          price: moneyToCents(line.cost?.amountPerQuantity),
          final_price: moneyToCents(line.cost?.amountPerQuantity),
          line_price: moneyToCents(line.cost?.subtotalAmount),
          final_line_price: moneyToCents(line.cost?.totalAmount),
          properties: Object.fromEntries((line.attributes || []).map(({ key, value }) => [key, value])),
        };
      }),
      variant_id: meta.variantId ? Number(meta.variantId) : undefined,
      quantity: meta.quantity,
    };
  }

  function publishDawnCart(cart, meta = {}) {
    if (typeof global.publish !== 'function' || !global.PUB_SUB_EVENTS?.cartUpdate) return;
    global.publish(global.PUB_SUB_EVENTS.cartUpdate, {
      source: meta.source || 'storefront-cart',
      cartData: dawnCart(cart, meta),
      productVariantId: meta.variantId ? String(meta.variantId) : undefined,
      variantId: meta.variantId ? String(meta.variantId) : undefined,
    });
  }

  function productUrl(line) {
    const handle = line.merchandise?.product?.handle || '';
    return `${rootUrl}/products/${encodeURIComponent(handle)}`;
  }

  function visibleOptions(line) {
    const options = (line.merchandise?.selectedOptions || []).filter(
      (option) => option?.value && option.value !== 'Default Title'
    );
    const attributes = (line.attributes || []).filter(
      (attribute) => attribute?.key && attribute.value && !attribute.key.startsWith('_')
    );
    const values = options.map(({ name, value }) => ({ name, value }));
    values.push(...attributes.map(({ key, value }) => ({ name: key, value })));
    if (line.sellingPlanAllocation?.sellingPlan?.name) {
      values.push({ name: strings.sellingPlan || 'Plan', value: line.sellingPlanAllocation.sellingPlan.name });
    }
    return values;
  }

  function renderOptions(line) {
    const options = visibleOptions(line);
    if (!options.length) return '';
    return `<dl class="product-option">${options
      .map(({ name, value }) => `<div><dt>${escapeHTML(name)}:</dt><dd>${escapeHTML(value)}</dd></div>`)
      .join('')}</dl>`;
  }

  function quantityLabel(template, productTitle) {
    return String(template || '').replaceAll('[product]', productTitle || '').replaceAll('__product__', productTitle || '');
  }

  function linePricePresentation(line) {
    const quantity = Math.max(1, Number(line?.quantity || 1));
    const totalMoney = line?.cost?.totalAmount || line?.merchandise?.price;
    const currencyCode = totalMoney?.currencyCode || config.currencyCode || 'USD';
    const totalAmount = Number(totalMoney?.amount || 0);
    const originalAmount = Math.max(
      Number(line?.cost?.subtotalAmount?.amount || 0),
      Number(line?.merchandise?.price?.amount || 0) * quantity,
      Number(line?.merchandise?.compareAtPrice?.amount || 0) * quantity
    );
    const hasOriginalPrice = originalAmount > totalAmount + 0.005;

    return {
      current: formatMoney(totalMoney),
      original: hasOriginalPrice ? formatMoney({ amount: originalAmount, currencyCode }) : '',
      isFreeGift: totalAmount <= 0.005 && hasOriginalPrice,
    };
  }

  function renderLine(line, index, context) {
    const merchandise = line.merchandise || {};
    const product = merchandise.product || {};
    const title = product.title || strings.product || 'Product';
    const image = merchandise.image;
    const quantityRule = merchandise.quantityRule || {};
    const minimum = Math.max(1, Number(quantityRule.minimum || 1));
    const increment = Math.max(1, Number(quantityRule.increment || 1));
    const maximum = Number(quantityRule.maximum || 0);
    const inputId = `${context === 'drawer' ? 'Drawer-' : ''}quantity-${index + 1}`;
    const rowId = `${context === 'drawer' ? 'CartDrawer-Item' : 'CartItem'}-${index + 1}`;
    const isDrawer = context === 'drawer';
    const price = linePricePresentation(line);
    const removeLabel = quantityLabel(strings.removeItem || 'Remove [product]', title);
    const decreaseLabel = quantityLabel(strings.decreaseQuantity || 'Decrease quantity for [product]', title);
    const increaseLabel = quantityLabel(strings.increaseQuantity || 'Increase quantity for [product]', title);
    const maxAttribute = maximum ? ` max="${maximum}"` : '';
    const imageMarkup = image?.url
      ? `<a href="${escapeHTML(productUrl(line))}" class="cart-item__link" tabindex="-1" aria-hidden="true"></a><img class="cart-item__image" src="${escapeHTML(image.url)}" alt="${escapeHTML(image.altText || title)}" loading="lazy" width="${Number(image.width || 160)}" height="${Number(image.height || 160)}">`
      : '';

    return `
      <tr class="cart-item${isDrawer && price.isFreeGift ? ' cart-item--gift' : ''}" id="${rowId}" data-storefront-cart-line="${escapeHTML(line.id)}">
        <td class="cart-item__media">
          <div class="cart-item__image-container gradient global-media-settings">${imageMarkup}</div>
        </td>
        <td class="cart-item__details">
          ${product.vendor ? `<p class="caption-with-letter-spacing">${escapeHTML(product.vendor)}</p>` : ''}
          <div class="cart-item__title-row">
            ${isDrawer && price.isFreeGift ? `<span class="cart-item__gift-badge">${escapeHTML(strings.freeGift || 'Free Gift')}</span>` : ''}
            <a href="${escapeHTML(productUrl(line))}" class="cart-item__name h4 break">${escapeHTML(title)}</a>
          </div>
          ${merchandise.title && merchandise.title !== 'Default Title' ? `<p class="product-option">${escapeHTML(merchandise.title)}</p>` : ''}
          ${renderOptions(line)}
          ${(line.discountAllocations || [])
            .map((discount) => `<p class="discounts__discount">${escapeHTML(discount.title || discount.code || strings.discount || 'Discount')}</p>`)
            .join('')}
        </td>
        <td class="cart-item__totals right">
          <div class="cart-item__price-wrapper">
            <span class="price price--end">${price.current}</span>
            ${isDrawer && price.original ? `<s class="cart-item__original-price">${price.original}</s>` : ''}
          </div>
        </td>
        <td class="cart-item__quantity">
          <div class="cart-item__quantity-wrapper">
            <quantity-input class="quantity cart-quantity">
              <button class="quantity__button" name="minus" type="button" aria-label="${escapeHTML(decreaseLabel)}"><span aria-hidden="true">-</span></button>
              <input class="quantity__input" type="number" id="${inputId}" value="${line.quantity}" min="${minimum}" step="${increment}"${maxAttribute} data-storefront-line-quantity data-line-id="${escapeHTML(line.id)}" data-quantity-variant-id="${numericId(merchandise.id) || ''}" aria-label="${escapeHTML(strings.quantity || 'Quantity')}">
              <button class="quantity__button" name="plus" type="button" aria-label="${escapeHTML(increaseLabel)}"><span aria-hidden="true">+</span></button>
            </quantity-input>
            <button class="button button--tertiary cart-remove-button" type="button" data-storefront-line-remove data-line-id="${escapeHTML(line.id)}" aria-label="${escapeHTML(removeLabel)}">${escapeHTML(strings.remove || 'Remove')}</button>
          </div>
        </td>
      </tr>
    `;
  }

  function renderTable(cart, context) {
    return `
      <div class="${context === 'drawer' ? 'drawer__cart-items-wrapper' : 'cart__items'}">
        <table class="cart-items" role="table">
          <caption class="visually-hidden">${escapeHTML(strings.cartTitle || 'Cart')}</caption>
          <thead><tr>
            <th><span class="visually-hidden">${escapeHTML(strings.image || 'Image')}</span></th>
            <th scope="col">${escapeHTML(strings.product || 'Product')}</th>
            <th class="right" scope="col">${escapeHTML(strings.total || 'Total')}</th>
            <th scope="col">${escapeHTML(strings.quantity || 'Quantity')}</th>
          </tr></thead>
          <tbody>${getLines(cart).map((line, index) => renderLine(line, index, context)).join('')}</tbody>
        </table>
      </div>
    `;
  }

  function applicableDiscounts(cart) {
    return (cart?.discountCodes || []).filter((discount) => discount?.code && discount.applicable);
  }

  function renderDiscountCodes(cart) {
    const discounts = applicableDiscounts(cart);
    if (!discounts.length) return '';
    return `<ul class="discounts list-unstyled" role="list">${discounts
      .map(
        ({ code }) => `<li class="discounts__discount discounts__discount--position"><span>${escapeHTML(code)}</span><button type="button" class="button button--tertiary" data-storefront-discount-remove data-discount-code="${escapeHTML(code)}" aria-label="${escapeHTML((strings.removeDiscount || 'Remove discount [code]').replace('[code]', code))}">${escapeHTML(strings.remove || 'Remove')}</button></li>`
      )
      .join('')}</ul>`;
  }

  function renderDiscountForm(cart, context) {
    const discountLabel = escapeHTML(strings.discount || 'Discount');
    return `
      <form class="cart-discount field" data-storefront-discount-form data-context="${context}">
        <label class="${context === 'drawer' ? 'visually-hidden' : 'field__label'}" for="StorefrontDiscount-${context}">${discountLabel}</label>
        <input class="field__input" id="StorefrontDiscount-${context}" name="discount" autocomplete="off" placeholder="${discountLabel}">
        <button class="button button--secondary" type="submit">${escapeHTML(strings.apply || 'Apply')}</button>
      </form>
      ${renderDiscountCodes(cart)}
    `;
  }

  function renderDrawerTool(type, label, content) {
    const headingId = `CartDrawerTool-${type}`;
    return `
      <div class="abok-cart-drawer__tool" data-cart-tool="${type}">
        <button class="abok-cart-drawer__tool-trigger" type="button" data-cart-tool-open aria-controls="${headingId}" aria-expanded="false" aria-haspopup="dialog"><span>${label}</span></button>
        <span class="abok-cart-drawer__tool-backdrop" data-cart-tool-close aria-hidden="true"></span>
        <div class="abok-cart-drawer__tool-sheet" id="${headingId}" data-cart-tool-sheet role="dialog" aria-modal="false" aria-labelledby="${headingId}-title" hidden>
          <div class="abok-cart-drawer__tool-header">
            <h3 class="abok-cart-drawer__tool-title" id="${headingId}-title">${label}</h3>
            <button class="abok-cart-drawer__tool-close" type="button" data-cart-tool-close aria-label="${escapeHTML(strings.close || 'Close')}"><span aria-hidden="true">&times;</span></button>
          </div>
          <div class="abok-cart-drawer__tool-body">${content}</div>
        </div>
      </div>
    `;
  }

  function renderSummary(cart, context) {
    const noteId = `StorefrontCartNote-${context}`;
    const noteLabel = escapeHTML(strings.note || 'Order note');
    const note = config.showCartNote
      ? context === 'drawer'
        ? `<form class="cart__note field" data-storefront-note-form><label class="visually-hidden" for="${noteId}">${noteLabel}</label><textarea class="text-area field__input" id="${noteId}" data-storefront-cart-note placeholder="${noteLabel}">${escapeHTML(cart?.note || '')}</textarea><button class="button button--secondary" type="submit" data-cart-note-apply>${escapeHTML(strings.apply || 'Apply')}</button></form>`
        : `<div class="cart__note field"><label for="${noteId}">${noteLabel}</label><textarea class="text-area field__input" id="${noteId}" data-storefront-cart-note>${escapeHTML(cart?.note || '')}</textarea></div>`
      : '';
    const totals = `
      ${renderDiscountForm(cart, context)}
      <div class="totals" role="status">
        <h2 class="totals__total">${escapeHTML(strings.estimatedTotal || 'Estimated total')}</h2>
        <p class="totals__total-value">${formatMoney(cart?.cost?.totalAmount)}</p>
      </div>
      <small class="tax-note caption-large rte">${escapeHTML(strings.taxNote || 'Taxes and shipping are calculated at checkout.')}</small>
      <div class="cart__ctas">
        <button type="button" class="cart__checkout-button button" data-storefront-checkout>${escapeHTML(strings.checkout || 'Check out')}</button>
      </div>
      <div class="cart-errors" data-storefront-cart-error role="alert"></div>
    `;
    if (context === 'drawer') {
      return `
        <div class="abok-cart-drawer__tools">
          ${config.showCartNote ? renderDrawerTool('note', noteLabel, note) : ''}
          ${renderDrawerTool('discount', escapeHTML(strings.discount || 'Discount'), renderDiscountForm(cart, context))}
        </div>
        <div class="cart-drawer__footer">
          <div class="abok-cart-drawer__summary">
            <small class="tax-note caption-large rte">${escapeHTML(strings.taxNote || 'Taxes and shipping are calculated at checkout.')}</small>
            <div class="totals" role="status"><h2 class="totals__total">${escapeHTML(strings.subtotal || 'Subtotal')}</h2><p class="totals__total-value">${formatMoney(cart?.cost?.totalAmount)}</p></div>
          </div>
          <div class="cart__ctas"><button type="button" class="cart__checkout-button button" data-storefront-checkout>${escapeHTML(strings.checkout || 'Check out')}</button></div>
          <div class="cart-errors" data-storefront-cart-error role="alert"></div>
        </div>
      `;
    }
    return `${note}<div class="cart__blocks">${totals}</div>`;
  }

  function renderEmptyContent(includeCloseButton = false) {
    return `
      <div class="drawer__inner-empty">
        <div class="cart-drawer__warnings center">
          <div class="cart-drawer__empty-content">
            <h2 class="cart__empty-text">${escapeHTML(strings.empty || 'Your cart is empty')}</h2>
            ${
              includeCloseButton
                ? `<button class="drawer__close" type="button" data-cart-drawer-close aria-label="${escapeHTML(strings.close || 'Close')}"><span aria-hidden="true">&times;</span></button>`
                : ''
            }
            <a href="${escapeHTML(config.routes?.allProductsUrl || '/collections/all')}" class="button">${escapeHTML(strings.continueShopping || 'Continue shopping')}</a>
            <div class="cart-errors" data-storefront-cart-error role="alert"></div>
          </div>
        </div>
      </div>
    `;
  }

  function renderDrawer(cart) {
    const mount = document.querySelector('[data-storefront-cart-drawer-content]');
    const drawer = document.querySelector('cart-drawer');
    if (!mount || !drawer) return;
    const isEmpty = getLines(cart).length === 0;
    drawer.classList.toggle('is-empty', isEmpty);
    const titleCount = drawer.querySelector('[data-storefront-cart-title-count]');
    const paidItemQuantity = getLines(cart).reduce(
      (total, line) => total + (linePricePresentation(line).isFreeGift ? 0 : Number(line.quantity || 0)),
      0
    );
    if (titleCount) titleCount.textContent = String(paidItemQuantity);
    if (isEmpty) {
      mount.innerHTML = renderEmptyContent(true);
      return;
    }
    mount.innerHTML = `
      <div class="abok-cart-drawer__shipping-status">
        <p>${escapeHTML(strings.freeShippingEligible || 'You are eligible for free shipping.')}</p>
        <span class="abok-cart-drawer__shipping-progress" aria-hidden="true"><span></span></span>
      </div>
      <cart-drawer-items class="drawer__contents">
        <form id="CartDrawer-Form" class="cart__contents cart-drawer__form" data-storefront-cart-form>
          <div id="CartDrawer-CartItems" class="drawer__contents">${renderTable(cart, 'drawer')}</div>
        </form>
      </cart-drawer-items>
      <div class="drawer__footer">${renderSummary(cart, 'drawer')}</div>
    `;
  }

  function renderCartPage(cart) {
    const itemsMount = document.querySelector('[data-storefront-cart-page-items]');
    const itemsRoot = document.querySelector('[data-storefront-cart-page]');
    const footerMount = document.querySelector('[data-storefront-cart-page-footer]');
    const footerRoot = document.querySelector('.abok-cart-footer');
    if (!itemsMount && !footerMount) return;
    const isEmpty = getLines(cart).length === 0;
    itemsRoot?.classList.toggle('is-empty', isEmpty);
    footerRoot?.classList.toggle('is-empty', isEmpty);
    if (itemsMount) {
      itemsMount.innerHTML = isEmpty
        ? `<div class="cart__warnings center"><h2 class="cart__empty-text">${escapeHTML(strings.empty || 'Your cart is empty')}</h2><a href="${escapeHTML(config.routes?.allProductsUrl || '/collections/all')}" class="button">${escapeHTML(strings.continueShopping || 'Continue shopping')}</a><div class="cart-errors" data-storefront-cart-error role="alert"></div></div>`
        : `<form id="cart" class="cart__contents" data-storefront-cart-form>${renderTable(cart, 'page')}</form>`;
    }
    if (footerMount) footerMount.innerHTML = isEmpty ? '' : renderSummary(cart, 'page');
  }

  function renderHeaderCount(cart) {
    const count = Number(cart?.totalQuantity || 0);
    document.querySelectorAll('[data-storefront-cart-count]').forEach((element) => {
      element.textContent = String(count);
      element.toggleAttribute('hidden', count === 0 && element.hasAttribute('data-hide-when-empty'));
    });
    document.querySelectorAll('[data-storefront-cart-count-wrapper]').forEach((element) => {
      element.toggleAttribute('hidden', count === 0);
    });
    document.querySelectorAll('[data-storefront-cart-link]').forEach((element) => {
      const labelTemplate = element.dataset.cartLabel || strings.cartCount || 'Cart, [count] items';
      element.setAttribute('aria-label', labelTemplate.replace('[count]', String(count)));
    });
  }

  function renderNotification(cart) {
    const notification = document.querySelector('cart-notification');
    const productMount = document.getElementById('cart-notification-product');
    const button = document.getElementById('cart-notification-button');
    if (!notification || !productMount) return;
    const line = getLines(cart).at(-1);
    productMount.innerHTML = line
      ? `<div class="cart-notification-product__image"><img src="${escapeHTML(line.merchandise?.image?.url || '')}" alt="" width="70" height="70"></div><div><h3 class="cart-notification-product__name h4">${escapeHTML(line.merchandise?.product?.title || '')}</h3><p>${escapeHTML(line.merchandise?.title === 'Default Title' ? '' : line.merchandise?.title || '')}</p></div>`
      : '';
    if (button) button.textContent = String(strings.viewCart || 'View cart ([count])').replace('[count]', String(cart?.totalQuantity || 0));
  }

  function render(cart = currentCart) {
    renderHeaderCount(cart);
    renderDrawer(cart);
    renderCartPage(cart);
    renderNotification(cart);
  }

  function commitCart(cart, meta = {}) {
    currentCart = cart || null;
    render(currentCart);
    const snapshot = publicSnapshot(currentCart);
    emit('updated', { cart: snapshot, source: meta.source || 'storefront-cart' });
    publishDawnCart(currentCart, meta);
    return snapshot;
  }

  function showError(error, source = 'storefront-cart') {
    const message = error?.message || strings.cartError || 'The cart could not be updated.';
    document.querySelectorAll('[data-storefront-cart-error]').forEach((element) => {
      element.textContent = message;
    });
    emit('error', { message, code: error?.code || 'STOREFRONT_CART_ERROR', source });
  }

  async function refreshInternal() {
    return commitCart(await fetchCartInternal(), { source: 'refresh' });
  }

  function refresh() {
    return enqueue(async () => {
      setBusy(true, 'refresh');
      try {
        return await refreshInternal();
      } catch (error) {
        showError(error, 'refresh');
        throw error;
      } finally {
        setBusy(false, 'refresh');
      }
    });
  }

  function addLines(lines, options = {}) {
    const normalizedLines = lines.map(normalizeLine);
    return enqueue(async () => {
      setBusy(true, options.source || 'add');
      try {
        await ajaxRequest(endpoints.add, { method: 'POST', body: { items: normalizedLines } });
        let cart = await fetchCartInternal();
        cart = await synchronizePaidAttributes(cart);
        const variantId = normalizedLines[0]?.id;
        return commitCart(cart, {
          source: options.source || 'product-form',
          variantId,
          quantity: normalizedLines[0]?.quantity,
        });
      } catch (error) {
        try {
          await refreshInternal();
        } catch (_refreshError) {
          // Preserve the original mutation error while best-effort reconciliation fails.
        }
        showError(error, options.source || 'add');
        throw error;
      } finally {
        setBusy(false, options.source || 'add');
      }
    });
  }

  function addFromForm(form, options = {}) {
    return addLines(linesFromForm(form).concat(options.extraLines || []), options);
  }

  function buyNowFromForm(form, options = {}) {
    const lines = linesFromForm(form).concat(options.extraLines || []).map(normalizeLine);
    const source = options.source || 'buy-now';
    setBusy(true, source);
    return enqueue(async () => {
      try {
        await ajaxRequest(endpoints.add, { method: 'POST', body: { items: lines } });
        return commitCart(await fetchCartInternal(), {
          source,
          variantId: lines[0]?.id,
          quantity: lines[0]?.quantity,
        });
      } catch (error) {
        try {
          await refreshInternal();
        } catch (_refreshError) {
          // Preserve the original mutation error while best-effort reconciliation fails.
        }
        throw error;
      }
    })
      .then(async (snapshot) => {
        if (typeof options.validate === 'function') await options.validate(snapshot);
        return enqueue(async () => {
          let cart = await fetchCartInternal();
          cart = await synchronizePaidAttributes(cart);
          const nextSnapshot = commitCart(cart, { source });
          global.location.assign(checkoutUrl);
          return nextSnapshot;
        });
      })
      .catch((error) => {
        showError(error, source);
        throw error;
      })
      .finally(() => setBusy(false, source));
  }

  function updateLine(lineId, quantity, options = {}) {
    const nextQuantity = Number.parseInt(quantity, 10);
    return enqueue(async () => {
      setBusy(true, options.source || 'line-update');
      try {
        const id = String(lineId || '').trim();
        if (!id) throw new StorefrontCartError(strings.cartUnavailable || 'The cart item is unavailable.');
        const nextCart = normalizeAjaxCart(await ajaxRequest(endpoints.change, {
          method: 'POST',
          body: { id, quantity: Number.isFinite(nextQuantity) && nextQuantity > 0 ? nextQuantity : 0 },
        }));
        return commitCart(nextCart, { source: options.source || 'cart-items' });
      } catch (error) {
        try {
          await refreshInternal();
        } catch (_refreshError) {
          // Preserve the original mutation error while best-effort reconciliation fails.
        }
        showError(error, options.source || 'line-update');
        throw error;
      } finally {
        setBusy(false, options.source || 'line-update');
      }
    });
  }

  function removeLine(lineId, options = {}) {
    return updateLine(lineId, 0, { ...options, source: options.source || 'line-remove' });
  }

  function setVariantQuantities(updates, options = {}) {
    return enqueue(async () => {
      setBusy(true, options.source || 'bulk-update');
      try {
        let cart = currentCart || (await fetchCartInternal());
        const entries = Object.entries(updates || {}).map(([variantId, quantity]) => [
          numericId(variantId),
          Math.max(0, Number.parseInt(quantity, 10) || 0),
        ]);
        const existingLines = getLines(cart);
        const quantityUpdates = {};
        const additions = [];
        for (const [variantId, quantity] of entries) {
          if (!variantId) continue;
          const merchandiseId = productVariantGid(variantId);
          const existing = existingLines.find(
            (line) =>
              line.merchandise?.id === merchandiseId &&
              !(line.attributes || []).length &&
              !line.sellingPlanAllocation
          );
          if (existing) quantityUpdates[existing.id] = quantity;
          else if (quantity > 0) additions.push({ id: variantId, quantity });
        }

        if (Object.keys(quantityUpdates).length) {
          cart = normalizeAjaxCart(await ajaxRequest(endpoints.update, {
            method: 'POST',
            body: { updates: quantityUpdates },
          }));
        }
        if (additions.length) {
          await ajaxRequest(endpoints.add, { method: 'POST', body: { items: additions } });
          cart = await fetchCartInternal();
        }
        cart = await synchronizePaidAttributes(cart);
        return commitCart(cart, { source: options.source || 'bulk-update' });
      } catch (error) {
        try {
          await refreshInternal();
        } catch (_refreshError) {
          // Preserve the original mutation error while best-effort reconciliation fails.
        }
        showError(error, options.source || 'bulk-update');
        throw error;
      } finally {
        setBusy(false, options.source || 'bulk-update');
      }
    });
  }

  function getVariantQuantity(variantId) {
    const gid = productVariantGid(variantId);
    return getLines().reduce((total, line) => total + (line.merchandise?.id === gid ? line.quantity : 0), 0);
  }

  function updateDiscountCodes(discountCodes, options = {}) {
    return enqueue(async () => {
      setBusy(true, options.source || 'discount');
      try {
        const codes = [...new Set((discountCodes || []).map((code) => String(code).trim()).filter(Boolean))];
        const nextCart = normalizeAjaxCart(await ajaxRequest(endpoints.update, {
          method: 'POST',
          body: { discount: codes.join(',') },
        }));
        const appliedCodes = new Set(
          nextCart.discountCodes
            .filter((discount) => discount.applicable)
            .map((discount) => discount.code.toLowerCase())
        );
        const rejected = codes.find((code) => !appliedCodes.has(code.toLowerCase()));
        const snapshot = commitCart(nextCart, { source: options.source || 'discount' });
        if (rejected) {
          throw new StorefrontCartError(
            String(strings.discountInvalid || 'Discount code [code] is not applicable.').replace('[code]', rejected),
            { code: 'DISCOUNT_NOT_APPLICABLE' }
          );
        }
        return snapshot;
      } catch (error) {
        showError(error, options.source || 'discount');
        throw error;
      } finally {
        setBusy(false, options.source || 'discount');
      }
    });
  }

  function applyDiscount(code, options = {}) {
    const existingCodes = (currentCart?.discountCodes || [])
      .filter((discount) => discount.applicable)
      .map((discount) => discount.code);
    return updateDiscountCodes([...existingCodes, code], options);
  }

  function removeDiscount(code, options = {}) {
    const nextCodes = (currentCart?.discountCodes || [])
      .filter((discount) => discount.applicable && discount.code !== code)
      .map((discount) => discount.code);
    return updateDiscountCodes(nextCodes, options);
  }

  function updateNote(note, options = {}) {
    return enqueue(async () => {
      setBusy(true, options.source || 'note');
      try {
        const nextCart = normalizeAjaxCart(await ajaxRequest(endpoints.update, {
          method: 'POST',
          body: { note: String(note || '') },
        }));
        return commitCart(nextCart, { source: options.source || 'note' });
      } catch (error) {
        showError(error, options.source || 'note');
        throw error;
      } finally {
        setBusy(false, options.source || 'note');
      }
    });
  }

  function checkout(options = {}) {
    return enqueue(async () => {
      setBusy(true, options.source || 'checkout');
      try {
        let cart = await fetchCartInternal();
        if (!getLines(cart).length) {
          throw new StorefrontCartError(strings.checkoutUnavailable || 'Checkout is unavailable.', {
            code: 'EMPTY_CART',
          });
        }
        cart = await synchronizePaidAttributes(cart);
        const snapshot = commitCart(cart, { source: options.source || 'checkout' });
        global.location.assign(checkoutUrl);
        return snapshot;
      } catch (error) {
        showError(error, options.source || 'checkout');
        throw error;
      } finally {
        setBusy(false, options.source || 'checkout');
      }
    });
  }

  function setTriggerLoading(trigger, loading) {
    if (!(trigger instanceof HTMLElement)) return;
    trigger.classList.toggle('loading', loading);
    trigger.toggleAttribute('aria-disabled', loading);
    if (trigger.matches('button')) trigger.toggleAttribute('disabled', loading);
    trigger.querySelector('.loading__spinner')?.classList.toggle('hidden', !loading);
  }

  function restoreDrawerTrap(toolType) {
    const drawer = document.querySelector('cart-drawer.active');
    if (!drawer) return;
    const summary = drawer.querySelector(`[data-cart-tool="${toolType}"] > [data-cart-tool-open]`);
    const container = document.getElementById('CartDrawer') || drawer;
    if (typeof global.trapFocus === 'function' && summary) {
      global.trapFocus(container, summary);
    } else {
      summary?.focus({ preventScroll: true });
    }
  }

  function closeDrawerTool(tool, restoreFocus = true) {
    if (!(tool instanceof HTMLElement)) return;
    const toolType = tool.dataset.cartTool || '';
    const summary = tool.querySelector(':scope > [data-cart-tool-open]');
    const sheet = tool.querySelector('[data-cart-tool-sheet]');
    tool.removeAttribute('data-cart-tool-opened');
    summary?.setAttribute('aria-expanded', 'false');
    sheet?.setAttribute('aria-modal', 'false');
    if (sheet) sheet.hidden = true;
    if (restoreFocus) global.requestAnimationFrame(() => restoreDrawerTrap(toolType));
  }

  function activateDrawerTool(tool) {
    if (!(tool instanceof HTMLElement)) return;
    const summary = tool.querySelector(':scope > [data-cart-tool-open]');
    const sheet = tool.querySelector('[data-cart-tool-sheet]');
    tool.setAttribute('data-cart-tool-opened', '');
    summary?.setAttribute('aria-expanded', 'true');
    sheet?.setAttribute('aria-modal', 'true');
    if (sheet) sheet.hidden = false;

    tool.parentElement?.querySelectorAll('[data-cart-tool][data-cart-tool-opened]').forEach((sibling) => {
      if (sibling !== tool) closeDrawerTool(sibling, false);
    });

    global.requestAnimationFrame(() => {
      if (!tool.isConnected || !tool.hasAttribute('data-cart-tool-opened')) return;
      const field = sheet?.querySelector('textarea, input') || sheet?.querySelector('[data-cart-tool-close]');
      if (typeof global.trapFocus === 'function' && field) global.trapFocus(tool, field);
      field?.focus({ preventScroll: true });
    });
  }

  function handleDrawerToolEscape(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;
    const openTool = document.querySelector('cart-drawer.active [data-cart-tool][data-cart-tool-opened]');
    if (!openTool) return;
    event.preventDefault();
    event.stopPropagation();
    closeDrawerTool(openTool);
  }

  function handleClick(event) {
    const toolSummary = event.target.closest('[data-cart-tool-open]');
    if (toolSummary) {
      event.preventDefault();
      const tool = toolSummary.closest('[data-cart-tool]');
      if (tool.hasAttribute('data-cart-tool-opened')) closeDrawerTool(tool);
      else activateDrawerTool(tool);
      return;
    }

    const toolClose = event.target.closest('[data-cart-tool-close]');
    if (toolClose) {
      event.preventDefault();
      closeDrawerTool(toolClose.closest('[data-cart-tool]'));
      return;
    }

    const closeButton = event.target.closest('[data-cart-drawer-close]');
    if (closeButton) {
      event.preventDefault();
      closeButton.closest('cart-drawer')?.close?.();
      return;
    }

    const removeButton = event.target.closest('[data-storefront-line-remove]');
    if (removeButton) {
      event.preventDefault();
      setTriggerLoading(removeButton, true);
      removeLine(removeButton.dataset.lineId)
        .catch(() => undefined)
        .finally(() => setTriggerLoading(removeButton, false));
      return;
    }

    const discountButton = event.target.closest('[data-storefront-discount-remove]');
    if (discountButton) {
      event.preventDefault();
      setTriggerLoading(discountButton, true);
      removeDiscount(discountButton.dataset.discountCode)
        .catch(() => undefined)
        .finally(() => setTriggerLoading(discountButton, false));
      return;
    }

    const checkoutButton = event.target.closest('[data-storefront-checkout]');
    if (checkoutButton) {
      event.preventDefault();
      setTriggerLoading(checkoutButton, true);
      checkout()
        .catch(() => undefined)
        .finally(() => setTriggerLoading(checkoutButton, false));
    }
  }

  function handleChange(event) {
    const quantityInput = event.target.closest('[data-storefront-line-quantity]');
    if (quantityInput) {
      const quantity = Number.parseInt(quantityInput.value, 10);
      const min = Number.parseInt(quantityInput.min || '1', 10);
      const max = Number.parseInt(quantityInput.max || '0', 10);
      const step = Number.parseInt(quantityInput.step || '1', 10);
      if (quantity < min || (max && quantity > max) || (quantity - min) % step !== 0) {
        const quantityError = String(strings.quantityError || 'Enter a valid quantity.').replace(
          '[quantity]',
          Number.isFinite(quantity) ? String(quantity) : ''
        );
        quantityInput.setCustomValidity(quantityError);
        const currentLine = getLines().find((line) => line.id === quantityInput.dataset.lineId);
        if (currentLine) quantityInput.value = currentLine.quantity;
        quantityInput.reportValidity();
        return;
      }
      quantityInput.setCustomValidity('');
      updateLine(quantityInput.dataset.lineId, quantity).catch(() => render(currentCart));
      return;
    }

    const noteInput = event.target.closest('[data-storefront-cart-note]');
    if (noteInput) {
      if (noteInput.closest('[data-storefront-note-form]')) return;
      global.clearTimeout(noteTimers.get(noteInput));
      updateNote(noteInput.value).catch(() => render(currentCart));
    }
  }

  function handleInput(event) {
    const noteInput = event.target.closest('[data-storefront-cart-note]');
    if (!noteInput || noteInput.closest('[data-storefront-note-form]')) return;
    global.clearTimeout(noteTimers.get(noteInput));
    noteTimers.set(
      noteInput,
      global.setTimeout(() => updateNote(noteInput.value).catch(() => render(currentCart)), 500)
    );
  }

  function handleSubmit(event) {
    const noteForm = event.target.closest('[data-storefront-note-form]');
    if (noteForm) {
      event.preventDefault();
      const button = event.submitter || noteForm.querySelector('[type="submit"]');
      const noteInput = noteForm.querySelector('[data-storefront-cart-note]');
      setTriggerLoading(button, true);
      updateNote(noteInput?.value || '', { source: 'note-form' })
        .catch(() => render(currentCart))
        .finally(() => setTriggerLoading(button, false));
      return;
    }

    const discountForm = event.target.closest('[data-storefront-discount-form]');
    if (!discountForm) return;
    event.preventDefault();
    const button = event.submitter || discountForm.querySelector('[type="submit"]');
    const input = discountForm.elements.discount;
    const code = String(input?.value || '').trim();
    if (!code) return;
    setTriggerLoading(button, true);
    applyDiscount(code, { source: 'discount-form' })
      .then(() => {
        input.value = '';
      })
      .catch(() => undefined)
      .finally(() => setTriggerLoading(button, false));
  }

  function bindEvents() {
    document.addEventListener('click', handleClick);
    document.addEventListener('change', handleChange);
    document.addEventListener('input', handleInput);
    document.addEventListener('submit', handleSubmit);
    document.addEventListener('keyup', handleDrawerToolEscape, true);
    document.addEventListener('shopify:section:load', () => render(currentCart));
    document.addEventListener('visitorConsentCollected', () => {
      void enqueue(async () => {
        const cart = await fetchCartInternal();
        commitCart(await synchronizePaidAttributes(cart), { source: 'consent' });
      }).catch(() => undefined);
    });
  }

  function init() {
    if (initialized) return readyPromise;
    initialized = true;
    bindEvents();
    render(null);
    readyPromise = refresh().catch(() => null);
    document.documentElement.dataset.storefrontCart = 'ready';
    return readyPromise;
  }

  const api = {
    init,
    ready: () => readyPromise || init(),
    refresh,
    addLines,
    addFromForm,
    buyNowFromForm,
    updateLine,
    removeLine,
    setVariantQuantities,
    getVariantQuantity,
    applyDiscount,
    removeDiscount,
    updateDiscountCodes,
    updateNote,
    checkout,
    getSnapshot: () => publicSnapshot(currentCart),
    toDawnCart: () => dawnCart(currentCart),
    formatMoney,
    StorefrontCartError,
  };

  global.AbokStorefrontCart = api;
  init();
})(window);
